import React from 'react';
import Icon from '../../../components/AppIcon';

const PaymentHistoryTimeline = ({ billingRecords, paymentAttempts }) => {
  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)}`;
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: 'CheckCircle',
      pending: 'Clock',
      failed: 'XCircle',
      cancelled: 'Ban',
      refunded: 'RotateCcw'
    };
    return icons?.[status] || 'Circle';
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'text-success',
      pending: 'text-warning',
      failed: 'text-destructive',
      cancelled: 'text-muted-foreground',
      refunded: 'text-primary'
    };
    return colors?.[status] || 'text-foreground';
  };

  // Combine billing records and payment attempts into timeline
  const timelineEvents = [
    ...billingRecords?.map(record => ({
      type: 'billing',
      date: record?.billing_date,
      status: record?.payment_status,
      amount: record?.total_amount,
      description: `Billing record - ${record?.payment_status}`,
      id: record?.id
    })),
    ...paymentAttempts?.map(attempt => ({
      type: 'attempt',
      date: attempt?.attempted_at,
      status: attempt?.attempt_status,
      amount: attempt?.amount,
      description: attempt?.failure_reason || `Payment attempt - ${attempt?.attempt_status}`,
      method: attempt?.payment_method,
      id: attempt?.id
    }))
  ]?.sort((a, b) => new Date(b?.date) - new Date(a?.date))?.slice(0, 20);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Clock" size={20} color="var(--color-primary)" />
        Payment History Timeline
      </h2>

      {timelineEvents?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No payment history available</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[500px] overflow-y-auto">
          {timelineEvents?.map((event, index) => (
            <div key={event?.id} className="flex gap-4">
              {/* Timeline Line */}
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                  event?.status === 'completed' ? 'bg-success/10 border-success' :
                  event?.status === 'failed' ? 'bg-destructive/10 border-destructive' :
                  event?.status === 'pending'? 'bg-warning/10 border-warning' : 'bg-muted border-border'
                }`}>
                  <Icon
                    name={getStatusIcon(event?.status)}
                    size={18}
                    className={getStatusColor(event?.status)}
                  />
                </div>
                {index < timelineEvents?.length - 1 && (
                  <div className="w-0.5 h-full bg-border mt-2" />
                )}
              </div>

              {/* Event Content */}
              <div className="flex-1 pb-6">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <p className="font-semibold text-foreground">
                      {event?.type === 'billing' ? 'Billing Record' : 'Payment Attempt'}
                    </p>
                    <p className="text-sm text-muted-foreground">{formatDate(event?.date)}</p>
                  </div>
                  <span className={`text-lg font-bold ${getStatusColor(event?.status)}`}>
                    {formatCurrency(event?.amount)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{event?.description}</p>
                {event?.method && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted rounded text-xs text-muted-foreground">
                    <Icon name="CreditCard" size={12} />
                    {event?.method}
                  </span>
                )}
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ml-2 ${
                  event?.status === 'completed' ? 'bg-success/10 text-success' :
                  event?.status === 'failed' ? 'bg-destructive/10 text-destructive' :
                  event?.status === 'pending'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                }`}>
                  {event?.status?.charAt(0)?.toUpperCase() + event?.status?.slice(1)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryTimeline;